import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ShoppingCart,
  FavoriteBorder,
  Favorite,
  Add,
  Remove,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BranchProduct } from "../../../types";
import RatingStars from "./RatingStars";
import ProductActionButtons from "./ProductActionButtons";

interface AnimatedProductCardProps {
  product: BranchProduct;
  isWishlist: boolean;
  quantity: number;
  currentImageIndex: number;
  isHovered: boolean;
  images: any[];
  maxQuantity: number;
  rating: number;
  badge: string | undefined;
  isFeatured: boolean;
  stockStatus: {
    text: string;
    color: string;
  };
  getCardColors: string;
  getBadgeColor: () => string;
  layoutVariant: string;
  t: (key: string, options?: any) => string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  toggleWishlist: (e: React.MouseEvent) => void;
  handleAddToCart: (qty: number) => void;
  handleWhatsAppPurchase: (qty: number) => void;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
  setCurrentImageIndex: (index: number) => void;
}

const AnimatedProductCard: React.FC<AnimatedProductCardProps> = ({
  product,
  isWishlist,
  quantity,
  currentImageIndex,
  isHovered,
  images,
  maxQuantity,
  rating,
  badge,
  isFeatured,
  stockStatus,
  getCardColors,
  getBadgeColor,
  layoutVariant,
  t,
  onMouseEnter,
  onMouseLeave,
  toggleWishlist,
  handleAddToCart,
  handleWhatsAppPurchase,
  setQuantity,
  setCurrentImageIndex,
}) => {
  const navigate = useNavigate();

  // WhatsApp Icon component
  const WhatsAppIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.507 14.307l-.009.075c-.016.15-.026.302-.026.456 0 2.757-2.243 5-5 5-.163 0-.323-.008-.482-.023l-.074-.009-4.187 1.521a.499.499 0 01-.646-.606l1.511-4.219c-2.346-1.336-3.897-3.776-3.897-6.494 0-4.142 3.358-7.5 7.5-7.5 4.142 0 7.5 3.358 7.5 7.5 0 2.943-1.693 5.494-4.162 6.743zm-5.507-12.307c-5.238 0-9.5 4.262-9.5 9.5 0 2.666 1.115 5.155 3.065 6.933l.137.116-1.081 3.175 3.158-1.149.116.067c1.725.963 3.648 1.458 5.605 1.458 5.238 0 9.5-4.262 9.5-9.5s-4.262-9.5-9.5-9.5z" />
    </svg>
  );

  return (
    <motion.div
      data-product-id={product.id}
      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Image Section */}
      <div className="relative aspect-square overflow-hidden">
        <div
          className="w-full h-full cursor-pointer"
          onClick={() => navigate(`/product/${product.product.id}`)}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={currentImageIndex}
              src={images[currentImageIndex]?.image || "/api/placeholder/500/500"}
              alt={product.product.name}
              className="w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
            />
          </AnimatePresence>
        </div>

        {/* Image Dots */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, idx) => (
              <button
                key={idx}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentImageIndex ? "bg-white w-6" : "bg-white/60"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(idx);
                }}
              />
            ))}
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {badge && (
            <span
              className={`text-xs font-bold px-3 py-1.5 rounded-full shadow-lg ${getBadgeColor()}`}
            >
              {badge}
            </span>
          )}
          {isFeatured && (
            <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg">
              Featured
            </span>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={toggleWishlist}
          className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2.5 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:scale-110"
        >
          {isWishlist ? (
            <Favorite className="w-5 h-5 text-red-500" />
          ) : (
            <FavoriteBorder className="w-5 h-5 text-gray-600" />
          )}
        </button>

        {/* Quick Buy Overlay */}
        {isHovered && product.is_available && (
          <motion.div
            className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex flex-col gap-3">
              <button
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3 rounded-full font-bold flex items-center gap-2 shadow-xl hover:scale-105 transition-transform"
                onClick={(e) => {
                  e.stopPropagation();
                  handleWhatsAppPurchase(quantity);
                }}
              >
                <WhatsAppIcon />
                {t("productCard.whatsappPurchase") || "الشراء المباشر من واتساب"}
              </button>

              {/* زر الذهاب إلى صفحة المنتج */}
              <button
                className="bg-white text-gray-800 px-8 py-3 rounded-full font-bold flex items-center gap-2 shadow-xl hover:bg-gray-50 transition-all"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/product/${product.product.id}`);
                }}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                {t("productCard.viewProduct") || "معاينة المنتج"}
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5">
        {/* Title & Rating */}
        <div className="mb-4">
          <h3
            className="text-xl font-bold text-gray-800 mb-2 cursor-pointer hover:text-primary transition-colors line-clamp-1"
            onClick={() => navigate(`/product/${product.product.id}`)}
          >
            {product.product.name}
          </h3>
          {rating > 0 && (
            <RatingStars rating={rating}  />
          )}
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-5 line-clamp-2">
          {product.product.description}
        </p>

        {/* Price & Stock */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="text-2xl font-bold text-primary">
              ج.م {product.price}
            </span>
            {Number(product.product.base_price) > Number(product.price) && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-400 line-through">
                  ج.م {product.product.base_price}
                </span>
                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                  وفر ج.م{" "}
                  {(product.product.base_price - product.price).toFixed(2)}
                </span>
              </div>
            )}
          </div>
          <span
            className={`text-sm font-medium px-3 py-1.5 rounded-full ${stockStatus.color}`}
          >
            {stockStatus.text}
          </span>
        </div>

        {/* Quantity & Actions */}
        <div className="space-y-4">
          {/* Quantity */}
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-700">
              {t("productCard.quantity") || "الكمية"}
            </span>
            <div className="flex items-center border rounded-lg">
              <button
                className="px-4 py-2 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
              >
                <Remove className="w-4 h-4" />
              </button>
              <span className="px-4 py-2 min-w-[50px] text-center font-bold text-lg">
                {quantity}
              </span>
              <button
                className="px-4 py-2 hover:bg-gray-50 disabled:opacity-40 transition-colors"
                onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                disabled={quantity >= maxQuantity}
              >
                <Add className="w-4 h-5" />
              </button>
            </div>
          </div>
{/* Action Buttons */}
<div className="space-y-4">


  {/* استدعاء مكون الأزرار */}
  <ProductActionButtons
    product={product}
    quantity={quantity}
    getCardColors={getCardColors}
    t={t}
    onAddToCart={handleAddToCart}
    onWhatsAppPurchase={handleWhatsAppPurchase}
  />
</div>
        </div>
      </div>
    </motion.div>
  );
};

export default AnimatedProductCard;